import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { env } from '@/config/env';
import { AppFooter } from '@/components/AppFooter';

/**
 * The sign-in page.
 *
 * @remarks
 * There is no form here, and there never will be. GitLab is the identity provider: this
 * product holds no passwords, offers no registration and has no "forgot password"
 * (`frontend-standards.md` section 5). The single button is a full page navigation, because
 * it ends at GitLab's own login form.
 *
 * The word "GitLab" appears deliberately. This screen is reached by everyone including
 * engineers, and the vocabulary firewall governs the **simple view** - hiding the identity
 * provider from someone being redirected to it would be confusing rather than clean. Every
 * screen behind sign-in that a non-technical person sees speaks product vocabulary only.
 */
export function SignInPage() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* minWidth: 0 because a flex child defaults to min-width: auto and will not shrink
          below its min-content width. Defensive rather than a fix for anything observed here:
          the card already shrinks correctly, but this is the usual cause of a page that
          scrolls sideways on a phone, and it costs nothing to rule out. */}
      <Stack flex={1} alignItems="center" justifyContent="center" sx={{ p: 2, minWidth: 0 }}>
        <Paper
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: 440,
            minWidth: 0,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* A single hairline of accent along the top edge. The only ornament on the page. */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              insetInline: 0,
              top: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)',
            }}
          />

          <Stack spacing={3} alignItems="center">
            <Box
              aria-hidden
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                border: 1,
                borderColor: 'divider',
                background: 'linear-gradient(140deg, rgba(34,211,238,0.16), transparent)',
                color: 'primary.main',
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              ⌘
            </Box>

            <Stack spacing={0.75}>
              <Typography variant="h1" sx={{ fontSize: '1.5rem' }}>
                سامانه کنترل پروژه
              </Typography>

              <Typography variant="body2" color="text.secondary">
                برای ورود، از حساب سازمانی خود استفاده کنید.
              </Typography>
            </Stack>

            <Button
              // A real link, not an onClick handler. The destination is another origin, so
              // the browser must perform a full navigation; an XHR would be blocked and a
              // programmatic redirect would break middle-click and "open in new tab".
              href={env.signInUrl}
              variant="contained"
              size="large"
              fullWidth
              sx={{ py: 1.25 }}
            >
              ورود با حساب سازمانی
            </Button>

            <Typography variant="caption" color="text.disabled">
              رمز عبوری در این سامانه ذخیره نمی‌شود.
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      {/* The version stamp belongs here too - this is the screen people are looking at when
          they report that something is wrong. */}
      <AppFooter />
    </Box>
  );
}
